/**
 * garage-recommendations.js — Factory 47 "Complete your build" (handlebar PDPs).
 *
 * HYBRID data model:
 *   - Baked (f47-companions.json): only STABLE, admin-only fields — cost (c),
 *     category (cat), finish (fin), fitment (f), universal (uni), last-price hint (p).
 *   - LIVE at render: price, inventory/availability, variant id, title, image come
 *     from Shopify's built-in /products/{handle}.js. So price & stock are never
 *     stale and sold-out items are never shown. The baked file only needs
 *     regenerating when cost changes or companions are added/removed.
 *
 * Block 1 "Complete your build": install companions, fitment-matched to the garage
 *   bike (or the bar's own fitment if no bike), within a mid-price band (live price
 *   <= ceiling% of the bar price), ranked by LIVE margin dollars (price - cost) desc.
 * Block 2 "Finish your build": F47 guards, fitment-matched, FINISH-matched to the bar
 *   (metafield-first, title fallback) via a fallback graph; NOT margin-ranked.
 *
 * Garage read + fit eval reuse window.F47Garage (garage-core). Flag-gated, additive.
 * Renders nothing on failure or when nothing qualifies.
 */
(function () {
  'use strict';

  var G = window.F47Garage;
  var mount = document.getElementById('f47-cyb');
  if (!G || !mount) return;

  // Relocate the block into the product-info column, directly under the buy button,
  // so it renders column-narrow (not full page width). Graceful: stays put if the
  // theme markup differs.
  (function relocate() {
    try {
      var anchor = document.querySelector('.product-info__add-to-cart') || document.querySelector('product-form.js-product-form-main');
      var block = anchor && anchor.closest ? anchor.closest('.product-info__block') : null;
      if (block && block.parentNode && !block.contains(mount)) { block.insertAdjacentElement('afterend', mount); }
    } catch (e) {}
  })();

  var cfg = {
    barHandle: mount.getAttribute('data-bar-handle') || '',
    barTitle: mount.getAttribute('data-bar-title') || '',
    barPrice: parseFloat(mount.getAttribute('data-bar-price')) || 0,
    barFinishRaw: mount.getAttribute('data-bar-finish') || '',
    barSlugs: (mount.getAttribute('data-bar-slugs') || '').split(/\s+/).filter(Boolean),
    pinned: (mount.getAttribute('data-bar-pinned') || '').split(/\s+/).filter(Boolean),
    excluded: (mount.getAttribute('data-bar-excluded') || '').split(/\s+/).filter(Boolean),
    catalogUrl: mount.getAttribute('data-catalog') || '',
    ceilingPct: parseFloat(mount.getAttribute('data-ceiling-pct')) || 0.70,
    b1Count: parseInt(mount.getAttribute('data-b1-count'), 10) || 3,
    b2Count: parseInt(mount.getAttribute('data-b2-count'), 10) || 3,
    b1Cats: (mount.getAttribute('data-b1-cats') || 'cable,brake,riser,grips,mirror').split(',').map(function (s) { return s.trim(); }).filter(Boolean)
  };
  var SHORTLIST = 12; // candidates per block to price-check live (bounded fetches)
  var CATALOG_COLS = []; // collection-id dictionary (for WPD collection-based pricing)

  function normFinish(s) {
    if (!s) return null;
    var l = String(s).toLowerCase();
    if (/black[\s\/_-]*chrome/.test(l)) return 'black-chrome';
    if (/chrome/.test(l)) return 'chrome';
    if (/black/.test(l)) return 'black';
    return null;
  }
  var FINISH_ACCEPT = { 'black': ['black', 'black-chrome'], 'chrome': ['chrome', 'black-chrome'], 'black-chrome': ['black-chrome', 'black', 'chrome'] };

  function fetchJSON(url) { return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }); }
  function money(n) { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(n); } catch (e) { return '$' + Number(n).toFixed(2); } }

  // Live product data (price/stock/variant/title/image) via Shopify's built-in endpoint.
  var liveCache = {};
  function fetchLive(handle) {
    if (liveCache[handle]) return liveCache[handle];
    liveCache[handle] = fetchJSON('/products/' + handle + '.js').then(function (pj) {
      if (!pj || !pj.variants) return null;
      var v = null;
      for (var i = 0; i < pj.variants.length; i++) { if (pj.variants[i].available) { v = pj.variants[i]; break; } }
      if (!v) return null; // out of stock -> never recommend
      var img = pj.featured_image || (pj.images && pj.images[0]) || (v.featured_image && v.featured_image.src) || '';
      return { handle: handle, title: pj.title, price: v.price / 100, vid: v.id, img: img, available: true, pid: pj.id, cap: v.compare_at_price || '' };
    });
    return liveCache[handle];
  }

  Promise.all([fetchJSON(cfg.catalogUrl), G.loadFitmentMap()]).then(function (res) {
    var catalog = res[0], map = res[1];
    if (!catalog || !catalog.items) return;
    CATALOG_COLS = catalog.cols || [];

    var slugToId = {}; (catalog.slugs || []).forEach(function (s, i) { slugToId[s] = i; });
    var vehicle = G.readActiveVehicle();
    var vSlugSet = G.resolveSlugs(vehicle, map);
    var targetSlugs = vSlugSet ? Array.from(vSlugSet) : cfg.barSlugs;
    var vIds = new Set();
    targetSlugs.forEach(function (s) { if (s in slugToId) vIds.add(slugToId[s]); });

    var excluded = {}; cfg.excluded.forEach(function (h) { excluded[h] = 1; });
    var pinRank = {}; cfg.pinned.forEach(function (h, i) { pinRank[h] = i; });
    function fitsBike(it) { if (it.uni === 1) return true; if (!it.f) return false; for (var i = 0; i < it.f.length; i++) { if (vIds.has(it.f[i])) return true; } return false; }

    var items = catalog.items.filter(function (it) { return it.h !== cfg.barHandle && !excluded[it.h]; });
    var ceiling = cfg.barPrice > 0 ? cfg.barPrice * cfg.ceilingPct : Infinity;

    // ---- Block 1 shortlist: fit + category, hint margin (p - c) desc, within hint band ----
    var b1cats = {}; cfg.b1Cats.forEach(function (c) { b1cats[c] = 1; });
    var b1pin = [], b1auto = [];
    items.forEach(function (it) {
      if (it.b !== 1 || !b1cats[it.cat]) return;
      if (it.h in pinRank) { b1pin.push(it); return; }
      if (!fitsBike(it)) return;
      if (typeof it.c !== 'number') return;
      if (it.p > ceiling) return;                 // loose pre-filter on hint price
      it._hm = it.p - it.c;
      b1auto.push(it);
    });
    b1auto.sort(function (a, b) { return b._hm - a._hm; });
    var b1short = b1pin.concat(b1auto.slice(0, SHORTLIST));

    // ---- Block 2 shortlist: F47 guards, fit, finish-match (fallback graph) ----
    var barFinish = normFinish(cfg.barFinishRaw) || normFinish(cfg.barTitle);
    var accept = barFinish ? FINISH_ACCEPT[barFinish] : null;
    var b2pin = [], b2auto = [];
    items.forEach(function (it) {
      if (it.b !== 2) return;
      if (it.h in pinRank) { b2pin.push(it); return; }
      if (!fitsBike(it)) return;
      if (!accept || !it.fin) return;
      var pri = accept.indexOf(it.fin);
      if (pri === -1) return;
      it._fp = pri; b2auto.push(it);
    });
    b2auto.sort(function (a, b) { return a._fp - b._fp; });
    var b2short = b2pin.concat(b2auto.slice(0, SHORTLIST));

    // ---- Fetch LIVE price/stock/variant for the shortlist, then finalize ----
    var wanted = {}; b1short.concat(b2short).forEach(function (it) { wanted[it.h] = 1; });
    Promise.all(Object.keys(wanted).map(fetchLive)).then(function () {
      Promise.all([finalizeBlock1(b1short, ceiling, pinRank), finalizeBlock2(b2short, pinRank)]).then(applyWPDPricing);
    });
  });

  // Let the Wholesale Pricing Discount app rewrite our card prices to the dealer
  // price (no-op / retail fallback if WPD isn't present for this customer).
  function applyWPDPricing() {
    if (typeof window.replaceWPDPrice !== 'function') return;
    try { window.replaceWPDPrice(Array.prototype.slice.call(mount.querySelectorAll('.f47-cyb-card__price'))); } catch (e) {}
  }

  function resolveLive(shortlist) {
    return Promise.all(shortlist.map(function (it) {
      return liveCache[it.h].then(function (live) { return live ? Object.assign({}, it, live) : null; });
    })).then(function (arr) { return arr.filter(Boolean); });
  }

  function finalizeBlock1(shortlist, ceiling, pinRank) {
    return resolveLive(shortlist).then(function (live) {
      var pinned = [], auto = [];
      live.forEach(function (it) {
        if (it.h in pinRank) { pinned.push(it); return; }        // pinned bypass band
        if (it.price > ceiling) return;                          // enforce LIVE price band
        it._lm = it.price - it.c;                                // LIVE margin
        auto.push(it);
      });
      pinned.sort(function (a, b) { return pinRank[a.h] - pinRank[b.h]; });
      auto.sort(function (a, b) { return b._lm - a._lm; });      // rank by live margin desc
      render(1, dedup(pinned.concat(auto)).slice(0, cfg.b1Count));
    });
  }

  function finalizeBlock2(shortlist, pinRank) {
    return resolveLive(shortlist).then(function (live) {
      var pinned = [], auto = [];
      live.forEach(function (it) { if (it.h in pinRank) pinned.push(it); else auto.push(it); });
      pinned.sort(function (a, b) { return pinRank[a.h] - pinRank[b.h]; });
      auto.sort(function (a, b) { return (a._fp || 0) - (b._fp || 0); }); // finish priority
      render(2, dedup(pinned.concat(auto)).slice(0, cfg.b2Count));
    });
  }

  function dedup(arr) { var seen = {}, out = []; arr.forEach(function (it) { if (!seen[it.h]) { seen[it.h] = 1; out.push(it); } }); return out; }

  function render(blockNo, list) {
    var block = mount.querySelector('.f47-cyb-block[data-block="' + blockNo + '"]');
    if (!block) return;
    if (!list.length) { block.hidden = true; return; }
    var ul = block.querySelector('.f47-cyb-list');
    ul.innerHTML = '';
    list.forEach(function (it) { ul.appendChild(card(it)); });
    block.hidden = false;
  }

  function card(it) {
    var li = document.createElement('li'); li.className = 'f47-cyb-card';
    var a = document.createElement('a'); a.className = 'f47-cyb-card__link'; a.href = '/products/' + it.h;
    if (it.img) { var img = document.createElement('img'); img.className = 'f47-cyb-card__img'; img.src = it.img; img.alt = it.title; img.loading = 'lazy'; img.width = 120; img.height = 120; a.appendChild(img); }
    var info = document.createElement('div'); info.className = 'f47-cyb-card__info';
    var t = document.createElement('span'); t.className = 'f47-cyb-card__title'; t.textContent = it.title;
    // WPD-processable price element (data attrs let replaceWPDPrice apply the dealer/
    // wholesale price); shows retail as the fallback when WPD isn't present.
    var p = document.createElement('span'); p.className = 'f47-cyb-card__price';
    p.setAttribute('data-wpd-product-handle', it.h);
    p.setAttribute('data-wpd-variant-id', it.vid);
    p.setAttribute('data-wpd-variant-price', Math.round(it.price * 100));
    p.setAttribute('data-wpd-variant-compare-at-price', it.cap || '');
    p.setAttribute('data-wpd-product-id', it.pid || '');
    p.setAttribute('data-wpd-product-collection-ids', [].concat(it.col || []).map(function (i) { return CATALOG_COLS[i]; }).filter(Boolean).join(','));
    p.innerHTML = '<span class="money">' + money(it.price) + '</span>';
    info.appendChild(t); info.appendChild(p); a.appendChild(info); li.appendChild(a);
    var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'f47-cyb-add btn btn--secondary';
    btn.textContent = 'Add'; btn.setAttribute('data-vid', it.vid); btn.setAttribute('aria-label', 'Add ' + it.title + ' to cart');
    btn.addEventListener('click', function () { addToCart(it.vid, btn); });
    li.appendChild(btn); return li;
  }

  function addToCart(vid, btn) {
    if (btn.disabled) return;
    btn.disabled = true; var orig = btn.textContent; btn.textContent = 'Adding…';
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }] }) })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function () { btn.textContent = 'Added ✓'; btn.classList.add('is-added'); refreshCart(); setTimeout(function () { btn.textContent = orig; btn.disabled = false; btn.classList.remove('is-added'); }, 2500); })
      .catch(function () { btn.textContent = 'Try again'; btn.disabled = false; setTimeout(function () { btn.textContent = orig; }, 2500); });
  }

  function refreshCart() {
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      var bubbles = document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count');
      for (var i = 0; i < bubbles.length; i++) { bubbles[i].textContent = cart.item_count; }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    }).catch(function () {});
  }
})();
